import React from 'react';
import { Check, Copy, MapPin } from 'lucide-react';

const demoAddresses = [
  {
    id: 'jp',
    country: 'Япония',
    city: '東京都',
    title: 'Для оформления аккаунта',
    lines: [
      'Prefecture  ： 東京都',
      'City            ： 江東区大島',
      'Address     ： 8-22-16',
      'Apartment ： ブルーサンシャイン１階',
      'Postal code： １３６－００７２',
      'Tel．         :      08032110121',
    ],
    phone: '08032110121',
    note: '',
    accent: 'from-neon-blue/40 via-accent/35 to-neon-yellow/30',
    flag: '🇯🇵',
  },
  {
    id: 'kg',
    country: 'Кыргызстан',
    city: 'Bishkek',
    title: 'Пункт выдачи (KG)',
    lines: ['36 А ул. Шопокова, Бишкек, Кыргызстан'],
    phone: '',
    note: '',
    accent: 'from-accent/45 via-neon-blue/30 to-white/5',
    flag: '🇰🇬',
  },
  {
    id: 'kz',
    country: 'Казахстан',
    city: 'Almaty',
    title: 'Пункт выдачи (KZ)',
    lines: ['Ул. Си Синхая 17 блок 2, Алматы, Казахстан'],
    phone: '',
    note: '',
    accent: 'from-neon-yellow/35 via-accent/30 to-neon-blue/25',
    flag: '🇰🇿',
  },
];

const AddressesPage = ({ userCountry }) => {
  const [tab, setTab] = React.useState('requisites');
  const [kzQrError, setKzQrError] = React.useState(false);
  const [copiedAddressId, setCopiedAddressId] = React.useState('');

  const copyText = async (text) => {
    const value = String(text || '').trim();
    if (!value) return false;

    const tgClipboard = window?.Telegram?.WebApp?.writeTextToClipboard;
    if (typeof tgClipboard === 'function') {
      await new Promise((resolve) => {
        tgClipboard(value, () => resolve());
      });
      return true;
    }

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  };

  const copyAddress = async (address) => {
    const ok = await copyText((address?.lines || []).join('\n'));
    if (!ok) return;
    setCopiedAddressId(address.id);
    window.setTimeout(() => setCopiedAddressId(''), 1800);
  };

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <div className="px-4 pt-6 max-w-5xl mx-auto">
        <h1 className="text-xl font-semibold tracking-tight">Реквизиты и адреса</h1>
        <p className="mt-2 text-sm text-text-secondary">Оплата и склады / пункты выдачи.</p>

        <div className="mt-4 inline-flex rounded-2xl border border-stroke bg-surface backdrop-blur-md p-1">
          <button
            type="button"
            onClick={() => setTab('requisites')}
            className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
              tab === 'requisites' ? 'bg-white/10 text-text-primary' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Реквизиты
          </button>
          <button
            type="button"
            onClick={() => setTab('addresses')}
            className={`px-4 py-2 rounded-2xl text-sm font-semibold transition ${
              tab === 'addresses' ? 'bg-white/10 text-text-primary' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Адреса
          </button>
        </div>

        {tab === 'requisites' ? (
          <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-extrabold tracking-tight">Оплата за вес</div>
            </div>

            {userCountry === 'KZ' ? (
              <div className="mt-5 grid grid-cols-1 gap-4">
                {!kzQrError ? (
                  <div className="rounded-2xl border border-stroke bg-black/10 p-4">
                    <img
                      src="/qr_kz1.jpg"
                      alt="QR для оплаты Казахстан"
                      className="w-full h-auto rounded-xl"
                      onError={() => setKzQrError(true)}
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl border border-stroke bg-black/10 p-6 flex flex-col items-center justify-center text-center min-h-[200px]">
                    <div className="text-3xl mb-3">🏗️</div>
                    <div className="text-sm font-semibold text-text-primary">QR-код для оплаты (Казахстан)</div>
                    <div className="text-xs text-text-secondary mt-1">Реквизиты скоро будут добавлены</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-stroke bg-black/10 p-4">
                  <img
                    src="/qr1.jpg"
                    alt="QR 1"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
                <div className="rounded-2xl border border-stroke bg-black/10 p-4">
                  <img
                    src="/qr2.jpg"
                    alt="QR 2"
                    className="w-full h-auto rounded-xl"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {demoAddresses.map((a) => (
              <div
                key={a.id}
                className="relative overflow-hidden rounded-2xl border border-stroke bg-surface backdrop-blur-md p-5"
              >
                <div className={`pointer-events-none absolute inset-0 opacity-70 bg-gradient-to-br ${a.accent}`} />
                <div className="relative">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs text-text-muted">{a.country} · {a.city}</div>
                      <div className="mt-1 text-base font-semibold text-text-primary">{a.title}</div>
                    </div>

                    <div className="h-12 w-12 rounded-2xl border border-stroke bg-white/5 flex items-center justify-center">
                      <span className="text-3xl">{a.flag}</span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2 text-sm">
                    {a.lines.map((l, idx) => (
                      <div key={idx} className="text-text-secondary">{l}</div>
                    ))}

                    <div className="mt-3 flex items-center gap-2 text-text-primary/90">
                      <MapPin size={16} className="text-neon-blue" />
                      <span className="text-sm">Адрес</span>
                    </div>

                    {a.phone ? <div className="mt-2 text-xs text-text-muted">Тел: {a.phone}</div> : null}

                    {a.id === 'jp' ? (
                      <button
                        type="button"
                        onClick={() => copyAddress(a)}
                        className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-neon-yellow/30 bg-neon-yellow/10 text-sm font-semibold text-neon-yellow transition hover:bg-neon-yellow/15"
                      >
                        {copiedAddressId === a.id ? <Check size={16} /> : <Copy size={16} />}
                        {copiedAddressId === a.id ? 'Адрес скопирован' : 'Скопировать адрес'}
                      </button>
                    ) : null}

                    {a.note && (
                      <div className="mt-3 text-xs text-text-primary/85 rounded-xl border border-stroke bg-black/15 p-3">
                        {a.note}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressesPage;

